import { useEffect, useRef } from "react";
import { usePhotoModerationUpload } from "../../lib";
import PhotoRequirements from "./PhotoRequirements";
import PhotoSuccess from "./PhotoSuccess";
import PhotoReject from "./PhotoReject";
import PhotoUploadButton from "./PhotoUploadButton";
import SubmitMessage from "../SubmitMessage";
import { PhotoUploadPayload } from "../../types";

interface PhotoUploadProps {
  photoModerationStep: string;
  setLoading: (loading: boolean) => void;
  setPhotoModerationStep: (step: string) => void;
  onPhotoReview?: (data: { imageUrl: string; imageHandshake: string }) => void;
  onPhotoConfirmed?: (data: { mediaUrl: string; mediaHandshake: string }) => void;
  headingText: string;
  paragraphText: string;
  reviewParagraphText: string;
  submitError?: string | null;
  uploadedPhotoData?: { mediaUrl: string; mediaHandshake: string };
}

/**
 * PhotoUpload handles the photo selection, moderation, and review flow
 * It manages three distinct states:
 * 1. Initial upload state with requirements display
 * 2. Photo review/confirmation state after successful upload
 * 3. Rejection state with reason and ability to retry
 *
 * The component is responsible for interfacing with the photo moderation service
 * and passing approved photos back to the parent for storage and display.
 */
const PhotoUpload: React.FC<PhotoUploadProps> = (props) => {
  const {
    photoModerationStep,
    setLoading,
    setPhotoModerationStep,
    onPhotoReview,
    onPhotoConfirmed,
    headingText,
    paragraphText,
    reviewParagraphText,
    submitError,
    uploadedPhotoData
  } = props;

  const photoModerationParams = usePhotoModerationUpload();
  const { handleFileChange, photoModResponse, photoModerationLoading, photoModerationStatus } =
    photoModerationParams;
  const errorRef = useRef<HTMLDivElement>(null);

  // Handles the response from photo moderation service and updates application flow
  // Routes to appropriate step based on moderation result
  const onPhotoUploadComplete = (data: PhotoUploadPayload) => {
    if (data?.imageHandshake && data?.imageUrl) {
      setPhotoModerationStep("photo-review");

      // Notify parent immediately for preview purposes
      if (onPhotoReview) {
        onPhotoReview({
          imageUrl: data.imageUrl,
          imageHandshake: data.imageHandshake
        });
      }
    } else if (!data?.imageHandshake && !data?.photoModeration?.accepted) {
      setPhotoModerationStep("photo-rejected");
    } else {
      setPhotoModerationStep("photo-pending");
    }
  };

  const onHandleFileChange =
    (callback: (data: PhotoUploadPayload) => void) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // Prevent multiple subsequent rejected photos from entering an unrecoverable loading state.
      photoModerationParams.resetPhotoModerationState();

      // Scroll to top of the page when user uploads a photo.
      window.scrollTo(0, 0);

      // Validate and upload.
      handleFileChange(event, callback);
    };

  const onPhotoConfirm = () => {
    // Use either photoModResponse or uploadedPhotoData
    if (photoModResponse?.imageUrl && photoModResponse?.imageHandshake) {
      if (onPhotoConfirmed) {
        onPhotoConfirmed({
          mediaUrl: photoModResponse.imageUrl,
          mediaHandshake: photoModResponse.imageHandshake
        });
      }
    } else if (uploadedPhotoData?.mediaUrl && uploadedPhotoData?.mediaHandshake) {
      if (onPhotoConfirmed) {
        onPhotoConfirmed({
          mediaUrl: uploadedPhotoData.mediaUrl,
          mediaHandshake: uploadedPhotoData.mediaHandshake
        });
      }
    }
  };

  // When photoModerationLoading changes, set loading state.
  // This allows the parent component to display a loading overlay.
  useEffect(() => {
    if (photoModerationLoading === true) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [photoModerationLoading, setLoading]);

  useEffect(() => {
    if (photoModerationStatus === "error") {
      setPhotoModerationStep("photo-rejected");
    }
  }, [photoModerationStatus, setPhotoModerationStep]);

  useEffect(() => {
    if (submitError && photoModerationStep === "photo-review" && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [submitError, photoModerationStep]);

  return (
    <div className="sak-engagement-flow__wrapper photo-upload">
      {/* Initial upload state - shows photo requirements and upload button */}
      {photoModerationStep === "photo-pending" && (
        <>
          <div className="sak-engagement-flow__header">
            <h2 className="sak-engagement-flow__header-text">{headingText}</h2>
          </div>

          <div className="sak-engagement-flow__body">
            <p>{paragraphText}</p>
          </div>

          <div className="sak-engagement-flow__controls">
            <PhotoUploadButton
              classList="btn btn-primary photo"
              clickEventActionKey="photo_upload_click"
              handleFileChange={onHandleFileChange(onPhotoUploadComplete)}
            >
              Share photo
            </PhotoUploadButton>
          </div>

          <PhotoRequirements />
        </>
      )}

      {/* Photo review state - shows uploaded photo with confirmation/retake options */}
      {photoModerationStep === "photo-review" && (
        <>
          <PhotoSuccess
            onConfirm={onPhotoConfirm}
            onRetake={onHandleFileChange(onPhotoUploadComplete)}
            paragraphText={reviewParagraphText}
          ></PhotoSuccess>

          {submitError && (
            <div ref={errorRef} style={{ scrollMarginBottom: "16px" }}>
              <style>
                {`
                  .submit-message.alert.photo {
                    margin-top: 0;
                    margin-bottom: 0;
                  }
                `}
              </style>
              <SubmitMessage
                visible={true}
                alertType="danger"
                message={submitError}
                wrapperClassName=""
                className="submit-message photo"
              />
            </div>
          )}
        </>
      )}

      {/* Rejection state - explains why photo was rejected and offers retry */}
      {photoModerationStep === "photo-rejected" && (
        <>
          <div className="sak-engagement-flow__header">
            <h2 className="sak-engagement-flow__header-text">{headingText}</h2>
          </div>
          <PhotoReject
            onRetake={onHandleFileChange(onPhotoUploadComplete)}
            photoModerationParams={photoModerationParams}
          ></PhotoReject>
          <PhotoRequirements inErrorState={true} />
        </>
      )}
    </div>
  );
};

export default PhotoUpload;
